// PackingSheetView.jsx — the printable packing sheet itself: header (invoice #,
// customer, order/date), items in the canonical Sunjin sort order (utils/orderSort),
// and the order note in a heavy box at the bottom. NO prices — this is the
// warehouse's sheet.
//
// Shared by the single-order page (/packing/:orderId) and the bulk page
// (/packing-bulk?ids=...), so the two can never drift apart.
//
// One-page fit: the item table's font size shrinks with the row count
// (14px down to a 10px floor) so most orders print on a single letter page.
// Only when even 10px can't fit does the list flow onto page 2.

import { sortOrderItems } from "../utils/orderSort";

export function formatPackingDate(ts) {
  if (!ts || !ts.toDate) return "—";
  return ts.toDate().toLocaleDateString("en-CA", {
    year: "numeric", month: "long", day: "numeric",
  });
}

// Pick the largest font size (14px..10px) whose rows all fit in the space
// left on one printed letter page below the header. Row height estimate:
// text line + vertical padding + 1px border. If nothing fits at 10px the
// table simply continues onto page 2.
function fitTableSize(rowCount) {
  const AVAILABLE = 760; // px of row space on one letter page below the header
  for (let font = 14; font >= 10; font--) {
    const pad = Math.max(2, Math.round(font * 0.28)); // vertical cell padding
    const rowHeight = Math.ceil(font * 1.35) + pad * 2 + 1;
    if (rowCount * rowHeight <= AVAILABLE) return { font, pad };
  }
  return { font: 10, pad: 2 }; // floor: keep 10px and let it spill to page 2
}

// Everything the sheet needs from an order + the product map, computed once
// so the pages can also show the "no storage method" warning for it.
export function preparePackingSheet(order, productsBySku) {
  const storageBySku = Object.fromEntries(
    Object.entries(productsBySku).map(([sku, p]) => [sku, p.storageMethod || ""])
  );
  const { sorted, missingStorageSkus: rawMissing } = sortOrderItems(order.items || [], storageBySku);
  // Credit lines (item 10000) aren't products — exclude from the warning.
  const missingStorageSkus = rawMissing.filter(sku =>
    sorted.some(i => String(i.sku) === sku && !i.creditLine));
  const { font, pad } = fitTableSize(sorted.length);
  return { sorted, missingStorageSkus, font, pad, invoiceNo: order.qbInvoiceNumber || null };
}

export default function PackingSheetView({ order, customerName, productsBySku }) {
  const { sorted, missingStorageSkus, font, pad, invoiceNo } = preparePackingSheet(order, productsBySku);

  return (
    <div
      className="packing-sheet"
      style={{ "--packing-font": `${font}px`, "--packing-pad": `${pad}px` }}
    >
      <div className="packing-header">
        <h1>Packing Sheet</h1>
        <p className="packing-invoice">
          Invoice #: <strong>{invoiceNo || "pending"}</strong>
        </p>
        <p className="packing-customer"><strong>{customerName || "Unknown customer"}</strong></p>
        <p className="packing-meta">
          Order {order.orderNumber} · {formatPackingDate(order.createdAt)}
        </p>
      </div>

      <table className="packing-table">
        <thead>
          <tr><th>SKU</th><th>Product</th><th className="qty-col">Qty</th></tr>
        </thead>
        <tbody>
          {sorted.map((item, idx) => {
            const p = productsBySku[String(item.sku)];
            const noStorage = missingStorageSkus.includes(String(item.sku));
            return (
              <tr key={idx} className={noStorage ? "no-storage-row" : ""}>
                <td>{item.sku}</td>
                <td>
                  {item.qty < 0 && <span className="return-tag">↩ RETURN </span>}
                  {item.freeGoods && <span className="free-tag">🎁 FREE </span>}
                  {item.creditLine
                    ? <>Credit — {item.creditDesc}</>
                    : (p?.name || "(unknown product)")}
                  {item.lineNote && (
                    <div className="line-note">📝 {item.lineNote}</div>
                  )}
                </td>
                <td className="qty-col">{item.qty}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Order note at the BOTTOM in a heavy black box (Brian, 2026-09-01):
          the packer reads the list top-down and the note is the last —
          unmissable — thing before the sheet leaves their hands. */}
      {order.comment && (
        <div className="packing-order-note">
          💬 <strong>NOTE:</strong> {order.comment}
        </div>
      )}
    </div>
  );
}
